import type { ScoringRunStep } from '../../types';

const TERMINAL_STATUSES = new Set([
  'completed',
  'Completed',
  'skipped',
  'failed',
  'Failed',
  'Stopped',
  'cancelled',
  'Expired',
]);

export interface StepClock {
  startedAt: string | null;
  finishedAt: string | null;
}

export interface StageClocks {
  summarizing: StepClock;
  classifying: StepClock;
  scoringBaseAndType: StepClock;
  scoringRelevance: StepClock;
  finalizing: StepClock;
}

const EMPTY_CLOCK: StepClock = { startedAt: null, finishedAt: null };

function indexSteps(steps: ScoringRunStep[]): Map<string, ScoringRunStep> {
  const byName = new Map<string, ScoringRunStep>();
  for (const step of steps) {
    if (step.step) byName.set(step.step, step);
  }
  if (!byName.has('scoring_base')) {
    const legacy = byName.get('scoring');
    if (legacy) byName.set('scoring_base', legacy);
  }
  return byName;
}

function earliestIso(values: Array<string | null | undefined>): string | null {
  let best: string | null = null;
  let bestMs = Infinity;
  for (const value of values) {
    if (!value) continue;
    const ms = Date.parse(value);
    if (Number.isNaN(ms)) continue;
    if (ms < bestMs) {
      bestMs = ms;
      best = value;
    }
  }
  return best;
}

function latestIso(values: Array<string | null | undefined>): string | null {
  let best: string | null = null;
  let bestMs = -Infinity;
  for (const value of values) {
    if (!value) continue;
    const ms = Date.parse(value);
    if (Number.isNaN(ms)) continue;
    if (ms > bestMs) {
      bestMs = ms;
      best = value;
    }
  }
  return best;
}

function clockOf(step: ScoringRunStep | undefined): StepClock {
  if (!step) return EMPTY_CLOCK;
  return {
    startedAt: step.started_at,
    finishedAt: step.finished_at,
  };
}

/** A step that has not started does not keep a sibling's finish time hidden. */
function holdsOpen(step: ScoringRunStep): boolean {
  const status = step.status || '';
  if (TERMINAL_STATUSES.has(status)) return false;
  if (!step.started_at && (status === '' || status === 'pending' || status === 'queued')) {
    return false;
  }
  return true;
}

function classifyingClock(byName: Map<string, ScoringRunStep>): StepClock {
  const parts = [byName.get('classification'), byName.get('escalation')].filter(
    (step): step is ScoringRunStep => Boolean(step),
  );
  const startedAt = earliestIso(parts.map((step) => step.started_at));
  if (parts.some(holdsOpen)) return { startedAt, finishedAt: null };
  const relevant = parts.filter(
    (step) => step.started_at || TERMINAL_STATUSES.has(step.status || ''),
  );
  if (!relevant.length || !relevant.every((step) => TERMINAL_STATUSES.has(step.status || ''))) {
    return { startedAt, finishedAt: null };
  }
  return {
    startedAt,
    finishedAt: latestIso(relevant.map((step) => step.finished_at)),
  };
}

export function stageClocks(steps: ScoringRunStep[]): StageClocks {
  const byName = indexSteps(steps);
  return {
    summarizing: clockOf(byName.get('summarization')),
    classifying: classifyingClock(byName),
    // Type score has no batch job. This row's clock is the base-score Bedrock job.
    scoringBaseAndType: clockOf(byName.get('scoring_base')),
    scoringRelevance: clockOf(byName.get('scoring_relevance')),
    finalizing: clockOf(byName.get('finalizing')),
  };
}
